import React, { useState, useEffect } from 'react';
import { getScheduledEmails } from '../../services/scheduleService';
import { getCampaigns } from '../../services/campaignService';

const DashboardOverview = () => {
  const [stats, setStats] = useState({
    totalEmailsSent: 0,
    pendingEmails: 0,
    sentEmails: 0,
    failedEmails: 0,
    totalCampaigns: 0,
    recentCampaigns: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch scheduled emails and campaigns
        const scheduledEmails = await getScheduledEmails();
        const campaigns = await getCampaigns();
        
        // Calculate stats
        const sentEmails = scheduledEmails.filter(email => 
          email.status === 'sent' || email.status === 'partial').length;
        const pendingEmails = scheduledEmails.filter(email => 
          email.status === 'pending').length;
        const failedEmails = scheduledEmails.filter(email => 
          email.status === 'failed').length;
          
        // Get recent campaigns (up to 5)
        const recentCampaigns = campaigns.slice(0, 5);
        
        setStats({
          totalEmailsSent: sentEmails,
          pendingEmails,
          sentEmails,
          failedEmails,
          totalCampaigns: campaigns.length,
          recentCampaigns
        });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
        Kunde inte ladda översiktsdata
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total Sent */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Totalt skickade
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              {stats.totalEmailsSent}
            </dd>
          </div>
        </div>
        
        {/* Pending */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Väntande
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-yellow-500">
              {stats.pendingEmails}
            </dd>
          </div>
        </div>
        
        {/* Successful */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Lyckades
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-green-500">
              {stats.sentEmails}
            </dd>
          </div>
        </div>
        
        {/* Failed */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 truncate">
              Misslyckades
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-red-500">
              {stats.failedEmails}
            </dd>
          </div>
        </div>
      </div>
      
      {/* Recent Campaigns */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Senaste kampanjer
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Dina senast skapade e-postkampanjer
          </p>
        </div>
        <div className="bg-white overflow-hidden">
          {stats.recentCampaigns.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Inga kampanjer har skapats än</p>
              <a href="#campaigns" 
                onClick={(e) => {
                  e.preventDefault();
                  window.location.hash = '#campaigns';
                }}
                className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Skapa kampanj
              </a>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {stats.recentCampaigns.map((campaign) => (
                <li key={campaign.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-indigo-600 truncate">
                      {campaign.title}
                    </p>
                    <div className="ml-2 flex-shrink-0 flex">
                      <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        {new Date(campaign.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex">
                      <p className="text-sm text-gray-500 truncate">
                        {campaign.subject}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-3">
            Snabbåtgärder
          </h3>
          <div className="flex flex-wrap gap-3">
            <a 
              href="#schedule"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = '#schedule';
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Skicka e-post
            </a>
            <a 
              href="#campaigns"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = '#campaigns';
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Hantera kampanjer
            </a>
            <a 
              href="#customers"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = '#customers';
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Hantera kunder
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
